import { CaretLeft, Error, Lock, Warning } from '@clearkit/icons';
import classNames from 'classnames';
import {
  CKBannerNext,
  CKBox,
  CKButton,
  CKCheckboxNext,
  CKLayoutHorizontal,
  CKSkeleton,
} from 'clearkit';
import { capitalize } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '~/components/app/appMainRoutes';
import CustomStripeCCInput, {
  CustomStripeCCInputRef,
  FormData,
} from '~/components/billing/CustomStripeCCInput';
import { CreditSelector } from '~/components/CreditSelector';
import { PageLayout } from '~/components/PageLayout';
import { GrowthPlan } from '~/components/PlanColumns';
import { useCurrentUser } from '~/components/profile/useCurrentUser';
import { useStatusToast } from '~/components/unified/hooks';
import {
  AccountFlagEnum,
  AccountPlanEnum,
  BillingInfo,
  useAccountBillingInfoQuery,
  useAccountUpdateBillingInfoMutation,
  useUpgradeAccountPlanMutation,
} from '~/generated/graphql';
import { useFeatureFlag } from '~/hooks/account';
import { useCreditUsage } from '~/hooks/account/useCreditUsage';
import useCreateStripeToken from '~/hooks/useCreateStripeToken';
import {
  creditOptions,
  HUBSPOT_FREE_TIER_CREDITS,
  LEGACY_FREE_TIER_CREDITS,
} from '~/lib/selfServePlanHelpers';
import { usePublishableKeys } from '~/pages/Batches/hooks/usePublishableKeys';

export const LoadingUpgradeSkeleton = () => {
  return (
    <div className="p-4">
      <CKSkeleton className="w-1/3 mb-4 rounded h-[2rem]" isLoading />

      <CKSkeleton className="w-1/4 mb-4 rounded h-[2.5rem]" isLoading />

      <div className="mb-4">
        <CKSkeleton className="mb-2 rounded h-[2rem]" isLoading />
        <CKSkeleton className="mb-2 rounded h-[2rem]" isLoading />
        <CKSkeleton className="rounded h-[2rem]" isLoading />
      </div>

      <CKSkeleton className="w-3/4 mb-4 rounded h-[2.5rem]" isLoading />

      <CKSkeleton className="w-1/2 mb-4 rounded h-[1.5rem]" isLoading />

      <CKSkeleton className="w-1/4 rounded h-[3rem]" isLoading />
    </div>
  );
};

const GrowthUpgrade = ({
  account,
  refetchAccount,
}: {
  account: any;
  refetchAccount: () => void;
}) => {
  const {
    isHubspotAccount,
    planId: currentPlanId,
    credits,
    isPaidAccount,
  } = useCreditUsage();

  const defaultCreditGrant = 1000;
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const { publishableKeys } = usePublishableKeys();
  const stripeCCInputRef = useRef<CustomStripeCCInputRef>(null);
  const [ccErrorMessage, setCCErrorMessage] = useState<string | null>(null);
  const [tosError, setTosError] = useState(false);
  const [isEditingCC, setIsEditingCC] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [creditGrantValue, setCreditGrantValue] = useState<number>(
    defaultCreditGrant,
  );

  const { mutate: createStripeToken } = useCreateStripeToken();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const showInvoiceFailedBanner = useFeatureFlag(
    AccountFlagEnum.LastInvoiceFailed,
  ).enabled;

  const lastInvoiceSucceeded = useFeatureFlag(
    AccountFlagEnum.LastInvoiceSucceeded,
  ).enabled;

  const { data: billingData, refetch } = useAccountBillingInfoQuery();

  const [accountBilling, setAccountBillingInfo] = useState<
    BillingInfo | undefined
  >(billingData?.accountBillingInfo);

  useEffect(() => {
    setAccountBillingInfo(billingData?.accountBillingInfo);
    setIsEditingCC(!billingData?.accountBillingInfo.cardLastFour);
  }, [billingData]);

  useEffect(() => {
    if (currentPlanId && credits && isPaidAccount) {
      setCreditGrantValue(credits);
    }
  }, [currentPlanId, credits]);

  const { addSuccessToast, addErrorToast } = useStatusToast();

  const [upgradeAccountPlan] = useUpgradeAccountPlanMutation({
    onCompleted: (resp) => {
      if (resp.upgradeAccountPlan?.errors?.length) {
        setIsSubmitting(false);
        addErrorToast({
          description: resp.upgradeAccountPlan?.errors[0].messages[0],
          heading: 'Error',
        });
        return;
      } else {
        addSuccessToast({
          description:
            'Your subscription limit will be updated when your invoice has been successfully processed. You will receive an email confirmation shortly.',
          heading: 'Subscription Updated',
        });
      }

      setIsSubmitting(false);
      refetchAccount();
    },
  });

  const upgradeToGrowth = async () => {
    let planId;

    if (isHubspotAccount) {
      planId =
        creditGrantValue === HUBSPOT_FREE_TIER_CREDITS
          ? AccountPlanEnum.HubspotFreeTier
          : AccountPlanEnum.HubspotPaidTier;
    } else {
      planId =
        creditGrantValue === LEGACY_FREE_TIER_CREDITS
          ? AccountPlanEnum.FreeTier
          : AccountPlanEnum.PaidTier;
    }

    return await upgradeAccountPlan({
      variables: {
        input: {
          planId,
          creditGrant: creditGrantValue,
        },
      },
    });
  };

  const [updateBillingInfo] = useAccountUpdateBillingInfoMutation();

  const onSubmit = async () => {
    if (!tosChecked) {
      setTosError(true);
      return;
    }

    setIsSubmitting(true);
    setFormErrors({});

    const formData = stripeCCInputRef.current?.getFormData() as FormData;

    const newFormErrors: Partial<FormData> = {};
    if (formData) {
      Object.keys(formData).forEach((key) => {
        const formDataKey = key as keyof FormData;
        if (!formData[formDataKey]) {
          const readableKey = formDataKey
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase();
          newFormErrors[formDataKey] = `${capitalize(readableKey)} is required`;
        }
      });
    }
    setFormErrors(newFormErrors);

    if (Object.keys(newFormErrors).length) {
      setIsSubmitting(false);
      return;
    }

    // Do not submit upgrade if there is no card saved
    if (formData && isEditingCC) {
      const cardData = {
        number: formData.creditCardNumber,
        exp_month: parseInt(formData.expiration.split('/')[0]),
        exp_year: parseInt(formData.expiration.split('/')[1]),
        cvc: formData.cvv,
        name: formData.fullName,
        address_line1: formData.billingAddress,
        address_city: formData.city,
        address_state: formData.state,
        address_zip: formData.zipcode,
      };

      createStripeToken(
        {
          cardData,
          stripePublishableKey: publishableKeys.STRIPE_PUBLISHABLE_KEY,
        },
        {
          onSuccess: async (result: any) => {
            if (result.error) {
              setCCErrorMessage(result?.error?.message as string);
              setIsSubmitting(false);
            } else {
              setCCErrorMessage(null);
              await updateBillingInfo({
                variables: {
                  input: {
                    cardToken: result.id,
                  },
                },
              });

              await upgradeToGrowth();
              refetch();
            }
          },
        },
      );
    } else if (accountBilling?.cardLastFour) {
      upgradeToGrowth();
    }
  };

  const selectedCreditOptions = creditOptions(isHubspotAccount);

  const isLoading = !publishableKeys || !publishableKeys.STRIPE_PUBLISHABLE_KEY;
  const isDirty = credits !== Number(creditGrantValue);

  const userFacingPlanName = isPaidAccount ? 'growth' : 'free';

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageLayout.Heading>Plans & Billing</PageLayout.Heading>
        <PageLayout.SubHeading>
          {isLoading ? (
            'Loading...'
          ) : (
            <>
              Your team — <strong>{account.name}</strong> — is currently on the{' '}
              {capitalize(userFacingPlanName)} plan
              {isPaidAccount ? ` with ${credits} credits per month` : ''}.
            </>
          )}
        </PageLayout.SubHeading>
      </PageLayout.Header>
      <PageLayout.Body>
        {showInvoiceFailedBanner ? (
          <CKBannerNext isFullWidth variant="error">
            <CKBannerNext.IconStatus>
              <Error />
            </CKBannerNext.IconStatus>
            <CKBannerNext.Body>
              An error occurred when processing your payment. Please re-enter
              your card information and try again.
            </CKBannerNext.Body>
          </CKBannerNext>
        ) : null}

        {!showInvoiceFailedBanner && !lastInvoiceSucceeded && isPaidAccount ? (
          <CKBannerNext isFullWidth variant="warning">
            <CKBannerNext.IconStatus>
              <Warning />
            </CKBannerNext.IconStatus>
            <CKBannerNext.Body>
              Invoice has not yet been processed. You will receive email
              confirmation shortly after processing and your credit limit will
              be increased.
            </CKBannerNext.Body>
          </CKBannerNext>
        ) : null}

        <PageLayout.Content className="max-w-[75rem]">
          {isLoading ? (
            <LoadingUpgradeSkeleton />
          ) : (
            <CKLayoutHorizontal
              alignItems="start"
              gap="2rem"
              justifyContent="start"
            >
              <GrowthPlan className="max-w-[20rem]" />
              <div className="space-y-12 grow">
                <CreditSelector
                  onChange={(value) => setCreditGrantValue(value)}
                  options={selectedCreditOptions}
                  value={creditGrantValue}
                />

                <div>
                  <span className="block w-full mb-4 text-base font-medium text-gray-600">
                    Enter your payment information
                  </span>
                  <CKBox className="px-6 py-4">
                    {ccErrorMessage && isEditingCC && (
                      <span className="mb-2 text-base font-medium text-red-500">
                        {ccErrorMessage}
                      </span>
                    )}
                    {accountBilling?.cardLastFour && !isEditingCC ? (
                      <div className="flex flex-row items-center justify-between mb-4 text-base text-gray-700">
                        <div className="flex flex-row items-center">
                          <span
                            className={classNames(
                              'p-2 border border-gray-200 rounded mr-2',
                            )}
                          >
                            ************{accountBilling.cardLastFour}
                          </span>
                          <span>{accountBilling.billingEmail}</span>
                        </div>
                        <CKButton
                          onClick={() => {
                            setFormErrors({});
                            setIsEditingCC(true);
                          }}
                          variant="tertiary"
                          variantColor="blue"
                        >
                          Update
                        </CKButton>
                      </div>
                    ) : (
                      <CustomStripeCCInput
                        className="pb-4"
                        errors={formErrors}
                        ref={stripeCCInputRef}
                      />
                    )}
                    <div className="flex flex-row items-center text-base text-gray-700">
                      <Lock className="mr-2" />
                      <span>Payments securely processed by Stripe</span>
                    </div>
                  </CKBox>
                </div>

                <div className={'pb-24 text-base'}>
                  <span
                    className={classNames(
                      'block w-full mb-4 font-medium text-gray-600',
                      { 'text-red-500': tosError },
                    )}
                  >
                    Read and accept the Clearbit terms & conditions
                  </span>
                  <CKBox className="px-6 py-4">
                    <CKCheckboxNext
                      onChange={(checked) => {
                        setTosError(false);
                        setTosChecked(checked);
                      }}
                    >
                      I have read and agree to the{' '}
                      <a
                        href="https://clearbit.com/legal"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Clearbit TOS
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://clearbit.com/privacy-policy"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        privacy policy
                      </a>
                    </CKCheckboxNext>
                  </CKBox>
                </div>
              </div>
            </CKLayoutHorizontal>
          )}
        </PageLayout.Content>
      </PageLayout.Body>
      <PageLayout.Footer className="flex items-center justify-between bg-white gap-4">
        <CKButton
          as={Link}
          className="basis-auto shrink-0"
          leftIcon={<CaretLeft />}
          to={routes.plans}
          variant="tertiary"
          variantColor="blue"
        >
          Back to plans
        </CKButton>
        <CKLayoutHorizontal wrap="nowrap">
          {isDirty ? (
            <span className="text-sm text-gray-500">
              You’re upgrading to the Clearbit Growth plan with{' '}
              {creditGrantValue} credits for $
              {
                creditOptions(isHubspotAccount).find(
                  (option) => option.credits === creditGrantValue,
                )?.cost
              }{' '}
              per month. Credits refreshed monthly. Cancel anytime.
            </span>
          ) : null}
          <CKButton
            className="basis-auto shrink-0"
            isDisabled={isSubmitting || (isPaidAccount && !isDirty)}
            isLoading={isSubmitting}
            onClick={onSubmit}
            variant="bold"
            variantColor="green"
          >
            {isSubmitting ? 'Processing...' : 'Upgrade Plan'}
          </CKButton>
        </CKLayoutHorizontal>
        <div id="hidden-card-element" style={{ display: 'none' }}></div>
      </PageLayout.Footer>
    </PageLayout>
  );
};

export const PageTeamGrowthUpgrade = () => {
  const {
    account,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useCurrentUser();

  if (accountLoading || !account) {
    return <LoadingUpgradeSkeleton />;
  }

  return <GrowthUpgrade account={account} refetchAccount={refetchAccount} />;
};
